import React, { useEffect, useState } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, CheckCircle, XCircle, Clock, Image as ImageIcon, Star, MessageSquare, TrendingUp } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const STAT_CONFIG = [
  { key: 'total_users',            label: 'Total Users',         icon: Users,          color: '#3b82f6', border: '#bfdbfe', grad: '#eff6ff' }, // Blue
  { key: 'total_members',          label: 'Approved Members',    icon: CheckCircle,    color: '#10b981', border: '#a7f3d0', grad: '#f0fdf4' }, // Green
  { key: 'pending_applications',   label: 'Pending Review',      icon: Clock,          color: '#f59e0b', border: '#fde68a', grad: '#fffbeb' }, // Amber
  { key: 'rejected_applications',  label: 'Rejected',            icon: XCircle,        color: '#ef4444', border: '#fecaca', grad: '#fef2f2' }, // Red
  { key: 'total_listings',         label: 'Total Listings',      icon: ImageIcon,      color: '#8b5cf6', border: '#ddd6fe', grad: '#faf5ff' }, // Purple
  { key: 'active_listings',        label: 'Active Listings',     icon: TrendingUp,     color: '#14b8a6', border: '#99f6e4', grad: '#f0fdfa' }, // Teal
  { key: 'featured_listings',      label: 'Featured',            icon: Star,           color: '#ec4899', border: '#fbcfe8', grad: '#fdf2f8' }, // Pink
  { key: 'total_enquiries',        label: 'Conversations',       icon: MessageSquare,  color: '#64748b', border: '#cbd5e1', grad: '#f8fafc' }, // Slate
];

function StatCard({ cfg, value, loading }) {
  const Icon = cfg.icon;
  
  return (
    <div className="p-5 rounded-2xl flex flex-col justify-between h-[120px] shadow-sm hover:shadow-md transition-shadow" 
         style={{ 
           border: `1px solid ${cfg.border}`, 
           background: `linear-gradient(135deg, #ffffff 0%, ${cfg.grad} 100%)`,
           fontFamily: 'system-ui, -apple-system, sans-serif' 
         }}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-gray-500">{cfg.label}</p>
        <Icon size={16} style={{ color: cfg.color }} />
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-8 w-16 mb-1 bg-gray-100" />
        ) : (
          <h3 className="text-3xl font-bold text-gray-800 tracking-tight">{value ?? 0}</h3>
        )}
        <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">in total</p>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 bg-white border border-gray-100 shadow-xl rounded-xl" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <p className="font-semibold text-gray-800 mb-1 text-sm">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs font-medium" style={{ color: p.color || p.fill }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/admin/analytics')
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const ov = data?.overview || {};
  const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

  const outcomeData = [
    { label: 'Pending',  count: ov.pending_applications ?? 0,  fill: '#f59e0b' },
    { label: 'Approved', count: ov.approved_applications ?? 0, fill: '#10b981' },
    { label: 'Rejected', count: ov.rejected_applications ?? 0, fill: '#ef4444' },
  ];

  return (
    <DashboardShell title="Dashboard">
      <div className="p-6 space-y-8" data-testid="admin-analytics-panel" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>

        {error && (
          <div className="p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 text-sm">
            {error}
          </div>
        )}

        {/* ── Page intro ── */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard</h2>
            <p className="text-sm text-gray-500 mt-1">Welcome back! Here's an overview of your platform.</p>
          </div>
        </div>

        {/* ── Stat grid ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {STAT_CONFIG.map(cfg => (
            <StatCard key={cfg.key} cfg={cfg} value={ov[cfg.key]} loading={loading} />
          ))}
        </div>

        {/* ── Charts row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Applications over time */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-50">
              <h3 className="font-semibold text-gray-800 text-sm">Applications (Last 6 Months)</h3>
            </div>
            <div className="p-6">
              {loading ? (
                <Skeleton className="h-64 w-full bg-gray-50 rounded-xl" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data?.monthly_applications || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="blueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} dy={10} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} dx={-10} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="count" name="Applications" stroke="#3b82f6" strokeWidth={3} fill="url(#blueGrad)" activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Listings by category */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 flex flex-col">
            <div className="p-6 border-b border-gray-50">
              <h3 className="font-semibold text-gray-800 text-sm">Category Distribution</h3>
            </div>
            <div className="p-6 flex-1 flex items-center justify-center">
              {loading ? (
                <Skeleton className="h-48 w-48 rounded-full bg-gray-50" />
              ) : (data?.listings_by_category?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={data.listings_by_category}
                      dataKey="count"
                      nameKey="category"
                      cx="50%" cy="50%"
                      innerRadius={60}
                      outerRadius={85}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {data.listings_by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-sm">No data available</p>
              ))}
            </div>
          </div>
        </div>

        {/* ── Outcomes Row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Outcome bar chart */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-50">
              <h3 className="font-semibold text-gray-800 text-sm">Application Outcomes</h3>
            </div>
            <div className="p-6">
              {loading ? (
                <Skeleton className="h-40 w-full bg-gray-50 rounded-xl" />
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={outcomeData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} dy={10} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} dx={-10} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                      {outcomeData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Review Applications', sub: `${ov.pending_applications ?? 0} awaiting`, href: '/admin/applications', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
              { label: 'Manage Members',      sub: `${ov.total_members ?? 0} approved`,    href: '/admin/members',      color: '#10b981', bg: '#d1fae5', icon: Users },
              { label: 'All Listings',        sub: `${ov.active_listings ?? 0} active`,     href: '/admin/listings',     color: '#3b82f6', bg: '#dbeafe', icon: ImageIcon },
            ].map(action => (
              <a
                key={action.href}
                href={action.href}
                className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105" style={{ backgroundColor: action.bg, color: action.color }}>
                  <action.icon size={24} />
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">{action.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{action.sub}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

      </div>
    </DashboardShell>
  );
}
